import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ContentFeed = ({ posts, businessName }) => {
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [commentText, setCommentText] = useState({});
  const [postComments, setPostComments] = useState({});

  const handleLike = (postId) => {
    setLikedPosts((prev) => {
      const newSet = new Set(prev);
      if (newSet?.has(postId)) {
        newSet?.delete(postId);
      } else {
        newSet?.add(postId);
      }
      return newSet;
    });
  };

  const handleComment = (postId) => {
    if (!commentText?.[postId]?.trim()) return;

    const newComment = {
      id: Date.now(),
      author: "Usuario Actual",
      text: commentText?.[postId],
      timestamp: new Date()
    };

    setPostComments((prev) => ({
      ...prev,
      [postId]: [...(prev?.[postId] || []), newComment]
    }));

    setCommentText((prev) => ({ ...prev, [postId]: '' }));
  };

  const formatTimestamp = (date) => {
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return 'Hace un momento';
    if (diff < 3600) return `Hace ${Math.floor(diff / 60)} minutos`;
    if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} horas`;
    return `Hace ${Math.floor(diff / 86400)} días`;
  };

  return (
    <div className="bg-card rounded-lg shadow-card p-6 mb-6">
      <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
        <Icon name="Grid" size={24} color="var(--color-primary)" />
        Publicaciones Recientes
      </h2>
      <div className="space-y-6">
        {posts?.map((post) => {
          const currentLikes = post?.likes + (likedPosts?.has(post?.id) ? 1 : 0);
          const allComments = [...post?.comments, ...(postComments?.[post?.id] || [])];

          return (
            <div key={post?.id} className="border border-border rounded-lg overflow-hidden">
              {/* Post Header */}
              <div className="flex items-center gap-3 p-4 bg-muted/50">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                  <Icon name="Store" size={20} color="#FFFFFF" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-foreground">{businessName}</p>
                  <p className="text-xs text-muted-foreground">{formatTimestamp(post?.timestamp)}</p>
                </div>
              </div>
              {/* Post Content */}
              {post?.caption && (
                <div className="px-4 py-3">
                  <p className="text-foreground">{post?.caption}</p>
                </div>
              )}
              {/* Post Media */}
              {post?.type === 'image' && (
                <div className="bg-muted" style={{ height: '400px' }}>
                  <Image
                    src={post?.mediaUrl}
                    alt={post?.mediaAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              {post?.type === 'video' && (
                <div className="bg-black" style={{ height: '400px' }}>
                  <video
                    src={post?.mediaUrl}
                    controls
                    className="w-full h-full object-contain"
                    aria-label={post?.mediaAlt}
                  >
                    Tu navegador no soporta la reproducción de video.
                  </video>
                </div>
              )}
              {/* Post Actions */}
              <div className="p-4">
                <div className="flex items-center gap-4 mb-3">
                  <button
                    onClick={() => handleLike(post?.id)}
                    className="flex items-center gap-2 text-foreground hover:text-primary transition-colors"
                  >
                    <Icon
                      name={likedPosts?.has(post?.id) ? "Heart" : "Heart"}
                      size={24}
                      color={likedPosts?.has(post?.id) ? "#EF4444" : "currentColor"}
                      className={likedPosts?.has(post?.id) ? "fill-current" : ""}
                    />
                    <span className="font-medium">{currentLikes}</span>
                  </button>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Icon name="MessageCircle" size={24} />
                    <span className="font-medium">{allComments?.length}</span>
                  </div>
                </div>

                {/* Comments Section */}
                {allComments?.length > 0 && (
                  <div className="space-y-3 mb-3 max-h-60 overflow-y-auto">
                    {allComments?.map((comment) => (
                      <div key={comment?.id} className="bg-muted/50 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                            <Icon name="User" size={16} color="#FFFFFF" />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-sm text-foreground">{comment?.author}</p>
                            <p className="text-sm text-foreground mt-1">{comment?.text}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatTimestamp(comment?.timestamp)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Comment Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Escribe un comentario..."
                    value={commentText?.[post?.id] || ''}
                    onChange={(e) => setCommentText((prev) => ({ ...prev, [post?.id]: e?.target?.value }))}
                    onKeyPress={(e) => e?.key === 'Enter' && handleComment(post?.id)}
                    className="flex-1 px-4 py-2 bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <Button
                    variant="default"
                    size="default"
                    iconName="Send"
                    onClick={() => handleComment(post?.id)}
                    disabled={!commentText?.[post?.id]?.trim()}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ContentFeed;